// SignalsPage — what the outside world did, and the proof (BUILD-BRIEF-R2 §8, mockup "Signals").
//
// The page is one instrument: a rail of five counts, a band of filters, and three zones — the
// signals nobody has decided about, the monitors that produce them, and what was acted on this
// week. Nothing here writes to the record. The three verbs are the whole vocabulary:
//
//   Create task   a commitment owned by us, written by THIS click (the operator is its author)
//   Draft mail    a `draft` proposal, approved later in the Inbox — a machine never sends
//   Dismiss       nothing is written, and the fact that nothing was written is recorded
//
// HONEST STATES, because this page is mostly about absence:
//   · LINKEDIN POSTS reads `fv--unknown` "not collected" until a `linkedin_manual` monitor
//     exists. LinkedIn is never fetched (§8); the number is null, and null is not zero.
//   · SCOUT reads the worker THROUGH THE QUEUE. When its service cannot answer, the cell says
//     "not collected" rather than showing a comfortable zero fetches.
//   · A count of zero signals is `fv--nil`: we looked, and there were none. That is a different
//     fact and it looks different.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Att, Ev, Fig, Lnk, Mast, PageShell, Rail, RailCell, Record, Ref, Sep, Sheet, Tag, Tbl, Zone, ZoneHead,
} from '../components/PageKit.js';
import EmptyState from '../components/EmptyState.js';
import { useToast } from '../components/Toast.js';
import { severityTone } from '../components/SignalsZone.js';
import { useLocale, useT } from '../i18n/index.js';
import { ROUTES, open } from '../routes.js';
import { ApiError } from '../api/client.js';
import { dispose as disposeSignal, listSignals, signalsSummary, type SignalsSummary } from '../api/signals.js';
import {
  createMonitor, enablePreset, listMonitors, monitorPresets, runMonitor, updateMonitor,
} from '../api/monitors.js';
import type { Monitor, MonitorKind, MonitorPreset, Signal, SignalKind } from '../../shared/types.js';
import './SignalsPage.de.js';
import './SignalsPage.fr.js';

const KINDS: SignalKind[] = ['risk', 'budget', 'board', 'move', 'mention', 'tender', 'post', 'coverage'];
const MONITOR_KINDS: MonitorKind[] = [
  'website', 'careers', 'news', 'shab', 'zefix', 'surfe_job', 'tender', 'budget', 'linkedin_manual',
];

type StatusChip = 'new' | 'all';
type SubjectChip = '' | 'person' | 'organisation';

export default function SignalsPage() {
  const t = useT();
  const { fmtDate, fmtDateTime, fmtNum } = useLocale();
  const toast = useToast();

  const [summary, setSummary] = useState<SignalsSummary | null>(null);
  const [signals, setSignals] = useState<Signal[] | null>(null);
  const [acted, setActed] = useState<Signal[] | null>(null);
  const [monitors, setMonitors] = useState<Monitor[] | null>(null);
  const [presets, setPresets] = useState<MonitorPreset[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState<StatusChip>('new');
  const [subject, setSubject] = useState<SubjectChip>('');
  const [kind, setKind] = useState<SignalKind | ''>('');
  const [filter, setFilter] = useState('');

  const [busy, setBusy] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [newKind, setNewKind] = useState<MonitorKind>('website');
  const [newTarget, setNewTarget] = useState('');
  const [newCadence, setNewCadence] = useState('24');
  const [saving, setSaving] = useState(false);

  const sevenDaysAgo = useMemo(() => new Date(Date.now() - 7 * 864e5).toISOString(), []);

  const loadSignals = useCallback(async () => {
    try {
      const res = await listSignals({
        status: status === 'new' ? 'new' : undefined,
        kind: kind || undefined,
        subject_kind: subject || undefined,
        limit: 200,
      });
      setSignals(res.items);
      setError(null);
    } catch (err) {
      setSignals(null);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [status, kind, subject]);

  const loadRest = useCallback(async () => {
    const [s, a, m, p] = await Promise.allSettled([
      signalsSummary(),
      listSignals({ status: 'acted', since: sevenDaysAgo, limit: 50 }),
      listMonitors(),
      monitorPresets(),
    ]);
    // A failed poll is silence: the state stays null and the rail reads "not collected".
    setSummary(s.status === 'fulfilled' ? s.value : null);
    setActed(a.status === 'fulfilled' ? a.value.items : null);
    setMonitors(m.status === 'fulfilled' ? m.value.items : null);
    setPresets(p.status === 'fulfilled' ? p.value.items : null);
  }, [sevenDaysAgo]);

  useEffect(() => { void loadSignals(); }, [loadSignals]);
  useEffect(() => { void loadRest(); }, [loadRest]);

  const act = useCallback(async (signal: Signal, disposition: 'task' | 'draft' | 'dismissed') => {
    setBusy(signal.id);
    try {
      const updated = await disposeSignal(signal.id, disposition);
      toast('ok',
        disposition === 'task' ? t('Task {ref} created', { ref: updated.disposition_ref ?? '' })
        : disposition === 'draft' ? t('Draft {ref} filed for review', { ref: updated.disposition_ref ?? '' })
        : t('{ref} dismissed', { ref: updated.ref }));
      await Promise.all([loadSignals(), loadRest()]);
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not dispose of the signal.'));
    } finally {
      setBusy(null);
    }
  }, [loadSignals, loadRest, t, toast]);

  const submitMonitor = useCallback(async () => {
    const target = newTarget.trim();
    if (!target) return;
    setSaving(true);
    try {
      await createMonitor({ kind: newKind, target, cadence_hours: Number(newCadence) || 24 });
      setNewTarget('');
      setAddOpen(false);
      toast('ok', t('Monitor added'));
      await loadRest();
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not add the monitor.'));
    } finally {
      setSaving(false);
    }
  }, [newKind, newTarget, newCadence, loadRest, t, toast]);

  // "Run now" claims a run and answers; the sweep itself is minutes of politely spaced requests.
  // So the click reports that the run STARTED, then watches `last_run_at` change and reports the
  // outcome the run recorded. A watch that gives up says so — it never invents an outcome, and it
  // never fires a second run, which is what turned one register sweep into two.
  const runOne = useCallback(async (monitor: Monitor) => {
    setBusy(-monitor.id);
    const before = monitor.last_run_at ?? null;
    try {
      await runMonitor(monitor.id);
      toast('ok', t('{target}: run started', { target: monitor.target ?? String(monitor.id) }));
      await Promise.all([loadSignals(), loadRest()]);
      for (let attempt = 0; attempt < 40; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        let landed: Monitor | undefined;
        try {
          landed = (await listMonitors()).items.find((m) => m.id === monitor.id);
        } catch {
          break;                                   // a failed poll is silence, not a verdict
        }
        if (!landed || (landed.last_run_at ?? null) === before) continue;
        const outcome = landed.last_outcome ?? null;
        toast(outcome === 'error' ? 'err' : outcome === 'refused' ? 'warn' : 'ok',
          t('{target}: {outcome}', { target: monitor.target ?? String(monitor.id), outcome: t(outcome ?? 'done') })
          + (landed.last_error ? ` — ${landed.last_error}` : ''));
        await Promise.all([loadSignals(), loadRest()]);
        return;
      }
      toast('warn', t('{target}: still running — the outcome lands in the Monitors table.', {
        target: monitor.target ?? String(monitor.id),
      }));
      await loadRest();
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('The run failed.'));
    } finally {
      setBusy(null);
    }
  }, [loadSignals, loadRest, t, toast]);

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return signals ?? [];
    return (signals ?? []).filter((s) =>
      [s.headline, s.detail, s.quote, s.subject_label, s.ref].some((v) => (v ?? '').toLowerCase().includes(needle)));
  }, [signals, filter]);

  // ── the rail ──────────────────────────────────────────────────────────────
  const newKindDetail = summary?.new_by_kind.length
    ? summary.new_by_kind.map((r) => `${fmtNum(r.count)} ${t(r.kind)}`).join(' · ')
    : t('nothing waiting');
  const monitorDetail = summary?.monitors_by_kind.length
    ? summary.monitors_by_kind.slice(0, 4).map((r) => `${fmtNum(r.count)} ${t(r.kind)}`).join(' · ')
    : t('no target is watched yet');

  const scout = summary?.scout ?? null;

  return (
    <PageShell>
      <Mast
        kicker={t('Outreach')}
        title={t('Signals')}
        sub={summary ? (
          <>
            <Fig>{fmtNum(summary.monitors_total)}</Fig> {t('monitors')}
            <Sep />
            <Fig>{fmtNum(summary.runs_24h)}</Fig> {t('runs in 24h')}
            <Sep />{t('every signal cites what it was read from')}
            <Sep />{t('suggested actions are proposals')}
          </>
        ) : t('loading…')}
        verbs={
          <>
            <button type="button" className="btn" onClick={() => { setShowPresets((v) => !v); }}>
              {t('Presets')}
            </button>
            <button type="button" className="btn btn-accent" onClick={() => setAddOpen(true)}>{t('New monitor')}</button>
          </>
        }
      />

      <Rail after={
        <div className="instr-band">
          <div className="filter-chips" style={{ margin: 0 }}>
            <button type="button" className={`chip${status === 'new' ? ' active' : ''}`} aria-pressed={status === 'new'} onClick={() => setStatus('new')}>
              {t('New')} {summary && <Fig>{fmtNum(summary.new)}</Fig>}
            </button>
            <button type="button" className={`chip${status === 'all' ? ' active' : ''}`} aria-pressed={status === 'all'} onClick={() => setStatus('all')}>
              {t('All signals')}
            </button>
            <span style={{ width: 1, height: 16, background: 'var(--rule-2)' }} />
            {([['person', 'People'], ['organisation', 'Organisations']] as Array<[SubjectChip, string]>).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`chip${subject === key ? ' active' : ''}`}
                aria-pressed={subject === key}
                onClick={() => setSubject(subject === key ? '' : key)}
              >{t(label)}</button>
            ))}
            <span style={{ width: 1, height: 16, background: 'var(--rule-2)' }} />
            {KINDS.map((k) => (
              <button
                key={k}
                type="button"
                className={`chip${kind === k ? ' active' : ''}`}
                aria-pressed={kind === k}
                onClick={() => setKind(kind === k ? '' : k)}
              >{t(k)}</button>
            ))}
          </div>
          <div className="search-wrapper ml-auto" style={{ flex: '0 1 240px' }}>
            <input className="search-input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('Filter signals…')} />
          </div>
        </div>
      }>
        <RailCell
          label={t('New')}
          value={summary ? fmtNum(summary.new) : t('not collected')}
          tone={!summary ? 'unknown' : summary.new === 0 ? 'nil' : summary.new_by_severity.high > 0 ? 'broken' : 'attend'}
          detail={newKindDetail}
        />
        <RailCell
          label={t('Last 7d')}
          value={summary ? fmtNum(summary.last_7d) : t('not collected')}
          tone={!summary ? 'unknown' : summary.last_7d === 0 ? 'nil' : 'ink'}
          detail={summary
            ? `${fmtNum(summary.acted_7d)} ${t('acted on')} · ${fmtNum(summary.open_7d)} ${t('open')}`
            : t('no summary could be read')}
        />
        <RailCell
          label={t('Monitors')}
          value={summary ? fmtNum(summary.monitors_total) : t('not collected')}
          tone={!summary ? 'unknown' : summary.monitors_total === 0 ? 'nil' : summary.monitors_failed > 0 ? 'attend' : 'ink'}
          detail={monitorDetail}
        />
        <RailCell
          label={t('Scout')}
          // The worker is seen THROUGH the queue, never through a self-report. No answer at all
          // is "not collected"; an answer with a stale last claim is something to attend to.
          value={scout ? fmtNum(scout.queue_depth) : t('not collected')}
          unit={scout ? t('queued') : undefined}
          tone={!scout ? 'unknown' : scout.fresh ? 'ok' : 'attend'}
          detail={scout
            ? `${scout.worker ?? t('no worker')} · ${scout.last_claim_at ? t('last claim {when}', { when: fmtDateTime(scout.last_claim_at) }) : t('never claimed a job')} · ${fmtNum(scout.refused_24h)} ${t('refused in 24h')}`
            : t('the scout service has not answered')}
        />
        <RailCell
          label={t('LinkedIn posts')}
          // §8: LinkedIn is never fetched. Without a manual monitor nobody collected this, and
          // "not collected" is not zero.
          value={summary?.linkedin_posts != null ? fmtNum(summary.linkedin_posts) : t('not collected')}
          tone={summary?.linkedin_posts != null ? (summary.linkedin_posts === 0 ? 'nil' : 'ink') : 'unknown'}
          detail={t('manual import only · LinkedIn is never fetched')}
        />
      </Rail>

      {/* ── new signals ─────────────────────────────────────────────────── */}
      <Zone>
        <ZoneHead
          label={status === 'new' ? t('New signals') : t('All signals')}
          count={signals ? fmtNum(shown.length) : undefined}
          note={t('the quote is what the monitor read, character for character')}
        />
        {error ? (
          <EmptyState kind="error" title={t('Could not load')} detail={error} onRetry={loadSignals} />
        ) : signals === null ? (
          <Sheet pad><p className="zone-empty">{t('loading…')}</p></Sheet>
        ) : shown.length === 0 ? (
          <EmptyState
            kind={filter || kind || subject ? 'no-match' : 'empty'}
            title={filter || kind || subject ? t('Nothing matches') : t('Nothing waiting')}
            detail={filter || kind || subject
              ? t('No signal matches this filter.')
              : t('No signal is waiting for a decision. Monitors run on their cadence; add one below, or enable a preset.')}
            onClear={filter || kind || subject ? () => { setFilter(''); setKind(''); setSubject(''); } : undefined}
          />
        ) : (
          <Sheet clip>
            <Tbl>
              <thead>
                <tr>
                  <th style={{ width: 96 }}>{t('When')}</th>
                  <th style={{ width: 84 }}>{t('Kind')}</th>
                  <th style={{ width: 190 }}>{t('Concerns')}</th>
                  <th>{t('Signal · source · suggested action')}</th>
                  <th style={{ width: 190 }} />
                </tr>
              </thead>
              <tbody>
                {shown.map((signal) => (
                  <tr key={signal.id}>
                    <td className="voice-figure">{fmtDate(signal.first_seen)}</td>
                    <td><Tag tone={severityTone(signal.severity)}>{t(signal.kind)}</Tag></td>
                    <td>
                      {signal.subject_kind && signal.subject_id ? (
                        <Lnk onClick={() => open(signal.subject_kind === 'person'
                          ? ROUTES.person({ id: signal.subject_id as number })
                          : ROUTES.org({ id: signal.subject_id as number }))}
                        >{signal.subject_label ?? signal.subject_ref ?? `#${signal.subject_id}`}</Lnk>
                      ) : (
                        <span className="d n-none">—</span>
                      )}
                    </td>
                    <td>
                      <Record>{signal.headline}</Record>
                      {signal.quote ? (
                        <Ev src={<>
                          <Att kind="quoted" />
                          <Ref>{signal.evidence_ref ?? signal.ref}</Ref>
                          <Sep />
                          {t(signal.evidence_kind ?? 'manual')}
                          <Sep />
                          <Fig>{fmtDate(signal.first_seen)}</Fig>
                        </>}>{signal.quote}</Ev>
                      ) : (
                        // `computed` is arithmetic over rows we already hold: it makes no claim
                        // about the world, so it carries no quote and no claim mark.
                        <div className="rat">{t('computed from what the record already holds')}</div>
                      )}
                      {signal.detail && <div className="rat">{signal.detail}</div>}
                    </td>
                    <td>
                      {signal.disposition === 'none' ? (
                        <span className="flex gap-8 flex-wrap">
                          <button type="button" className="btn btn-sm" disabled={busy === signal.id} onClick={() => void act(signal, 'task')}>
                            {t('Create task')}
                          </button>
                          {signal.subject_kind === 'person' && (
                            <button type="button" className="btn btn-sm" disabled={busy === signal.id} onClick={() => void act(signal, 'draft')}>
                              {t('Draft mail')}
                            </button>
                          )}
                          <Lnk disabled={busy === signal.id} onClick={() => void act(signal, 'dismissed')}>{t('Dismiss')}</Lnk>
                        </span>
                      ) : (
                        <span className="d">
                          {t(signal.disposition)}
                          {signal.disposition_ref && <> <Ref>{signal.disposition_ref}</Ref></>}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tbl>
          </Sheet>
        )}
      </Zone>

      {/* ── monitors ────────────────────────────────────────────────────── */}
      <Zone id="monitors">
        <ZoneHead
          label={t('Monitors')}
          count={monitors ? fmtNum(monitors.length) : undefined}
          note={t('a refusal is recorded, never a silent skip')}
          route={<button type="button" className="zone-route" onClick={() => setAddOpen((v) => !v)}>{t('Add monitor')}</button>}
        />
        {addOpen && (
          <Sheet pad style={{ marginBottom: 'var(--s12)' }}>
            <div className="flex gap-8 flex-wrap" style={{ alignItems: 'flex-end' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="monitor-kind">{t('Kind')}</label>
                <select id="monitor-kind" className="form-select" value={newKind} onChange={(e) => setNewKind(e.target.value as MonitorKind)}>
                  {MONITOR_KINDS.map((k) => <option key={k} value={k}>{t(k)}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: '1 1 320px' }}>
                <label className="form-label" htmlFor="monitor-target">{t('Target — a URL, a UID or a query')}</label>
                <input
                  id="monitor-target"
                  className="form-input"
                  value={newTarget}
                  onChange={(e) => setNewTarget(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void submitMonitor(); }}
                  placeholder="https://…"
                />
              </div>
              <div className="form-group" style={{ width: 120 }}>
                <label className="form-label" htmlFor="monitor-cadence">{t('Cadence · hours')}</label>
                <input id="monitor-cadence" className="form-input" value={newCadence} inputMode="numeric" onChange={(e) => setNewCadence(e.target.value)} />
              </div>
              <button type="button" className="btn btn-accent" disabled={saving || !newTarget.trim()} onClick={() => void submitMonitor()}>
                {saving ? t('Saving…') : t('Add monitor')}
              </button>
              <Lnk onClick={() => setAddOpen(false)}>{t('Cancel')}</Lnk>
            </div>
            <p className="rat" style={{ marginTop: 'var(--s8)' }}>
              {t('Website, careers, tender and budget targets are fetched by the scout, which respects robots.txt and never logs in. SHAB and Zefix are read from their own APIs. LinkedIn is never fetched.')}
            </p>
          </Sheet>
        )}
        {monitors === null ? (
          <Sheet pad><p className="zone-empty">{t('The monitors could not be read.')}</p></Sheet>
        ) : monitors.length === 0 ? (
          <Sheet pad>
            <p className="zone-empty">{t('No monitor is configured. Add one above, or enable a preset — they ship disabled, because a monitor nobody asked for is egress nobody authorised.')}</p>
          </Sheet>
        ) : (
          <Sheet clip>
            <Tbl>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>{t('Kind')}</th>
                  <th>{t('Target')}</th>
                  <th style={{ width: 160 }}>{t('Concerns')}</th>
                  <th style={{ width: 90 }} className="num">{t('Cadence')}</th>
                  <th style={{ width: 140 }}>{t('Last run')}</th>
                  <th style={{ width: 110 }}>{t('Outcome')}</th>
                  <th style={{ width: 150 }} />
                </tr>
              </thead>
              <tbody>
                {monitors.map((monitor) => (
                  <tr key={monitor.id}>
                    <td><Tag tone={monitor.status === 'failed' ? 'broken' : monitor.status === 'paused' ? 'ghost' : 'neutral'}>{t(monitor.kind)}</Tag></td>
                    <td><span className="d">{monitor.target}</span>{monitor.last_error && <div className="rat">{monitor.last_error}</div>}</td>
                    <td>
                      {monitor.subject_label
                        ? <span className="d">{monitor.subject_label} <Ref>{monitor.subject_ref ?? ''}</Ref></span>
                        : <span className="d n-none">—</span>}
                    </td>
                    <td className="num"><Fig unit={t('h')}>{fmtNum(monitor.cadence_hours)}</Fig></td>
                    <td>{monitor.last_run_at ? <Fig>{fmtDateTime(monitor.last_run_at)}</Fig> : <span className="d">{t('never run')}</span>}</td>
                    <td>
                      {monitor.last_outcome
                        ? <Tag tone={monitor.last_outcome === 'signal' ? 'attend' : monitor.last_outcome === 'error' ? 'broken' : monitor.last_outcome === 'refused' ? 'ghost' : 'neutral'}>{t(monitor.last_outcome)}</Tag>
                        : <span className="d n-none">{t('not collected')}</span>}
                    </td>
                    <td>
                      <span className="flex gap-8">
                        <button type="button" className="btn btn-sm" disabled={busy === -monitor.id} onClick={() => void runOne(monitor)}>
                          {busy === -monitor.id ? t('Running…') : t('Run now')}
                        </button>
                        <Lnk onClick={() => void updateMonitor(monitor.id, { status: monitor.status === 'paused' ? 'active' : 'paused' }).then(loadRest)}>
                          {monitor.status === 'paused' ? t('Resume') : t('Pause')}
                        </Lnk>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tbl>
          </Sheet>
        )}
      </Zone>

      {/* ── presets ─────────────────────────────────────────────────────── */}
      {showPresets && (
        <Zone>
          <ZoneHead
            label={t('Presets')}
            count={presets ? fmtNum(presets.length) : undefined}
            note={t('one click each · they ship disabled')}
          />
          <Sheet clip>
            {(presets ?? []).map((preset) => (
              <div key={preset.id} className="row" style={{ gridTemplateColumns: '1fr 110px 120px' }}>
                <span>
                  <span className="w">{preset.label}</span>
                  <span className="d"> · {preset.target}</span>
                  <div className="rat">{preset.note}</div>
                </span>
                <span className="d">{t(preset.kind)} · <Fig unit={t('h')}>{fmtNum(preset.cadence_hours)}</Fig></span>
                <span>
                  {preset.enabled
                    ? <Tag tone="ok">{t('enabled')}</Tag>
                    : (
                      <button type="button" className="btn btn-sm" onClick={() => void enablePreset(preset.id).then(() => { toast('ok', t('Monitor added')); return loadRest(); })}>
                        {t('Enable')}
                      </button>
                    )}
                </span>
              </div>
            ))}
            {presets !== null && presets.length === 0 && <p className="zone-empty">{t('No preset is shipped.')}</p>}
          </Sheet>
        </Zone>
      )}

      {/* ── acted on ────────────────────────────────────────────────────── */}
      <Zone>
        <ZoneHead label={t('Acted on · 7d')} count={acted ? fmtNum(acted.length) : undefined} />
        <Sheet clip={!!acted?.length} pad={!acted?.length}>
          {acted === null ? (
            <p className="zone-empty">{t('This could not be read.')}</p>
          ) : acted.length === 0 ? (
            <p className="zone-empty">{t('Nothing was acted on this week.')}</p>
          ) : (
            acted.map((signal) => (
              <div key={signal.id} className="row" style={{ gridTemplateColumns: '96px 84px 190px 1fr 150px' }}>
                <Fig className="d">{fmtDate(signal.first_seen)}</Fig>
                <span><Tag>{t(signal.kind)}</Tag></span>
                <span>{signal.subject_label ?? <span className="d n-none">—</span>}</span>
                <span>{signal.headline}</span>
                <span className="d">
                  {t(signal.disposition)}
                  {signal.disposition_ref && <> <Ref>{signal.disposition_ref}</Ref></>}
                </span>
              </div>
            ))
          )}
        </Sheet>
      </Zone>
    </PageShell>
  );
}
